/** @format */

import {
  Box,
  InputAdornment,
  Button,
  TextField,
  styled,
  Typography,
  Snackbar,
  IconButton,
  Alert,
} from '@mui/material';

import Symbol from '../../../Assets/SVG/symbol';

import {
  AccountCircle,
  Close,
  HighlightOff,
  Https,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { isEmpty } from '../../../Util/utils';
import img from '../../../Assets/login.jpg';

import { userLoginKeycloak } from '../../../Services/keycloak/keycloak.service';

import {
  REALM_NAME,
  CLIENT_NAME,
  ACCESS_ROLES,
} from '../../../Constants/commons';

import ModalWrapper from '../../Molecules/Modal/ModalWrapper';

const LoginContainer = styled(Box, { label: 'loginPaper' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 24,
  padding: 24,
  width: '300px',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -70%)',
  borderRadius: '16px',

  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  transition: 'all 0.3s ease-in-out',

  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.25)',
  },
}));

function Login() {
  const [viewPassword, setViewPassword] = useState(false);

  const [password, setPassword] = useState({
    value: '',
    error: '',
  });

  const [username, setUsername] = useState({
    value: '',
    error: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: 'Invalid username or password',
    duration: 3000,
  });

  const [promptModal, setPromtModal] = useState(false);
  const [promptMessage, setPromptMessage] = useState('');

  const navigate = useNavigate();

  const handleLoginBtn = async () => {
    if (isEmpty(username.value)) {
      setUsername({
        ...username,
        error: 'Provide username!',
      });
      return;
    }

    if (isEmpty(password.value)) {
      setPassword({
        ...password,
        error: 'Provide password!',
      });
      return;
    }

    const params = new URLSearchParams();

    params.append('realm', REALM_NAME);
    params.append('clientId', CLIENT_NAME);
    params.append('username', username.value.trim());
    params.append('password', password.value.trim());

    try {
      const response = await userLoginKeycloak(params);
      const { data } = response;

      if (!data?.access_token) {
        setPromptMessage(
          'Login failed: access token was not returned by the server.'
        );
        setPromtModal(true);

        return;
      }

      const decoded = jwtDecode(data.access_token);
      // console.log('Decoded login data:', decoded);
      const {
        accountIds,
        clientIds,
        domainIds,
        branchIds,
        preferred_username,
      } = decoded;

      if (!accountIds?.length) {
        setPromptMessage(
          'Login succeeded, but account information is missing from your user profile.'
        );
        setPromtModal(true);

        return;
      }

      if (!clientIds?.length) {
        setPromptMessage(
          'Login succeeded, but client information is missing from your user profile.'
        );
        setPromtModal(true);

        return;
      }

      if (!domainIds?.length) {
        setPromptMessage(
          'Login succeeded, but domain information is missing from your user profile.'
        );
        setPromtModal(true);

        return;
      }

      if (!preferred_username) {
        setPromptMessage(
          'Login succeeded, but username information is missing from your user profile.'
        );
        setPromtModal(true);

        return;
      }

      const userDetails = {
        account_id: accountIds[0],
        client_id: clientIds[0],
        domain_id: domainIds[0],
        branch_id: branchIds,
        username: preferred_username,
        client_name: preferred_username,
      };

      localStorage.setItem(
        'USER_DETAILS',
        JSON.stringify(userDetails)
      );

      navigate('/', {
        replace: true,
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        setPromptMessage(
          'Unauthorized! Please check your credentials.'
        );
        setPromtModal(true);

        return;
      }

      setPromptMessage(
        error?.response?.data?.message ||
          'Something went wrong while logging in.'
      );

      setPromtModal(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleLoginBtn();
    }
  };

  const HelperText = ({ text }) => {
    return (
      <Typography variant="p" color="error">
        {text}
      </Typography>
    );
  };

  return (
    <>
      <Box
        component="section"
        sx={{
          position: 'relative',
          height: '100%',
          background: `linear-gradient(#00000090,#00000090),url(${img})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <LoginContainer elevation={3}>
          <Box>
            <Symbol
              width="40"
              height="40"
              color="#fafafa"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <TextField
              size="small"
              required
              type="text"
              label="username"
              variant="outlined"
              autoComplete="off"
              value={username.value}
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'white',
                },

                '& .MuiOutlinedInput-root': {
                  color: 'white',

                  '& fieldset': {
                    borderColor: 'white',
                  },
                },
              }}
              helperText={
                <HelperText text={username.error} />
              }
              error={Boolean(username.error)}
              onChange={(event) =>
                setUsername({
                  value: event.target.value,
                  error: '',
                })
              }
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ color: 'white' }}
                  >
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              size="small"
              required
              type={viewPassword ? 'text' : 'password'}
              label="password"
              variant="outlined"
              autoComplete="off"
              value={password.value}
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'white',
                },

                '& .MuiOutlinedInput-root': {
                  color: 'white',

                  '& fieldset': {
                    borderColor: 'white',
                  },
                },
              }}
              helperText={
                password.error ? (
                  <HelperText text={password.error} />
                ) : null
              }
              error={Boolean(password.error)}
              onChange={(event) =>
                setPassword({
                  value: event.target.value,
                  error: '',
                })
              }
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ color: 'white' }}
                  >
                    <Https />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment
                    onClick={() =>
                      setViewPassword(!viewPassword)
                    }
                    sx={{
                      cursor: 'pointer',
                      color: 'white',
                    }}
                    position="end"
                  >
                    {viewPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <Button
              sx={{ mt: 2 }}
              variant="contained"
              onClick={handleLoginBtn}
            >
              Login
            </Button>

            <Typography
              sx={{
                fontSize: '12px',
                color: 'white',
                cursor: 'pointer',
                textAlign: 'center',

                '&:hover': {
                  color: 'orange',
                },
              }}
              onClick={() =>
                navigate('/forgot-password')
              }
            >
              Forgot Password?
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={(theme) => ({
                fontSize: '12px',
                color: theme.palette.text.secondary,
              })}
            >
              Powered by
            </Typography>

            <Typography
              sx={{
                fontSize: '13px',
                color: 'skyblue',
                cursor: 'pointer',

                '&:hover': {
                  color: 'orange',
                },

                textDecoration: 'none',
              }}
              component="a"
              target="_new"
              href="https://navaantrix.com"
            >
              Navaantrix
            </Typography>
          </Box>
        </LoginContainer>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() =>
              setSnackbar((prev) => ({
                ...prev,
                open: false,
              }))
            }
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Alert
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ModalWrapper
        open={promptModal}
        handleClose={() =>
          setPromtModal(!promptModal)
        }
        size={{
          width: 250,
          height: 240,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            p: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            textAlign="center"
          >
            {promptMessage}
          </Typography>

          <Box sx={{ marginTop: 4 }}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<HighlightOff />}
              onClick={() =>
                setPromtModal(!promptModal)
              }
            >
              Close
            </Button>
          </Box>
        </Box>
      </ModalWrapper>
    </>
  );
}

export default Login;
